/**
 * Platform detection utilities.
 */

export type Platform = 'web' | 'ios' | 'android';

interface CapacitorGlobal {
  getPlatform?: () => string;
  isNativePlatform?: () => boolean;
}

function getCapacitor(): CapacitorGlobal | null {
  return (globalThis as { Capacitor?: CapacitorGlobal }).Capacitor ?? null;
}

/**
 * Get the current platform
 */
export function getPlatform(): Platform {
  return (getCapacitor()?.getPlatform?.() ?? 'web') as Platform;
}

/**
 * Check if running on a native platform (iOS or Android)
 */
export function isNativePlatform(): boolean {
  return getCapacitor()?.isNativePlatform?.() ?? false;
}

/**
 * Check if running on iOS
 */
export function isIOS(): boolean {
  return getPlatform() === 'ios';
}

/**
 * Check if running on Android
 */
export function isAndroid(): boolean {
  return getPlatform() === 'android';
}

/**
 * Check if running in web browser
 */
export function isWeb(): boolean {
  return getPlatform() === 'web';
}

/**
 * Get platform-specific value
 */
export function platformSelect<T>(options: {
  ios?: T;
  android?: T;
  web?: T;
  native?: T;
  default: T;
}): T {
  const platform = getPlatform();

  if (platform === 'ios' && options.ios !== undefined) {
    return options.ios;
  }

  if (platform === 'android' && options.android !== undefined) {
    return options.android;
  }

  if (platform === 'web' && options.web !== undefined) {
    return options.web;
  }

  if (isNativePlatform() && options.native !== undefined) {
    return options.native;
  }

  return options.default;
}
